(ns dashboard.entity.analysis.list
  (:require ["react" :as react]
            [dashboard.components.popup-menu :as popup-menu]
            [dashboard.entity.analysis.profile :as analysis.profile]
            [dashboard.ui :as ui]
            [re-frame.core :as rf]
            [reagent.core :as r]))

;; ----------------------------------------------------------------------------
;;  Analysis Table Components
;; ----------------------------------------------------------------------------

(defn maybe-uuid
  [value]
  (if (string? value) (parse-uuid value) value))

(defn AnalysisState
  [asa on-click]
  (let [id->state (update-keys (into {} (mapv maybe-uuid (:states asa)))
                               maybe-uuid)
        state (id->state (maybe-uuid (:state-id asa)))]
    [:button.button.is-rounded.is-seconadary
     {:title (str state ": click to edit")
      :class :analysis--state-button
      :on-click #(on-click (:construct-id asa) asa)}
     [:label
      {:class (analysis.profile/state-name->class state)}
      [analysis.profile/state-name->icon state]]]))

(defn AnalysisConstruct
  [{:keys [next-version-id construct-id construct]
    :as applied-analysis}
   & [on-click]]
  [(if on-click :a :b)
   {:class [:is-flex
            (when next-version-id
              "has-background-warning-light")]
    :on-click (fn [e]
                (when on-click
                  (.stopPropagation e)
                  (on-click construct-id applied-analysis)))}
   [:span.is-size-7.mb-0.mt-1.ml-2.mr-0 (analysis.profile/format-analysis-id construct-id)]
   [:span.ml-1
    construct
    (when next-version-id
      [:span.is-size-7
       " - "
       [:i.icon.fas.fa-square-pen]
       " outdated"])]])

(defn AnalysisCategoryTable
  [hide-category row-fn coll]
  (->> coll
       (group-by (comp :name first :categories))
       (mapv (fn [[category category-asas]]
               [:<>
                (when-not hide-category
                  [:h6.title.is-6.my-0 category])
                (->> category-asas
                     (mapv row-fn)
                     (into [:<>]))]))
       (into [:div.questions--analysis])))

;; ----------------------------------------------------------------------------
;;  Analysis Question Components
;; ----------------------------------------------------------------------------

(defn SuggestedAnalyses [suggested-structured-analyses
                         question
                         questionnaire-id
                         on-report-suggestion
                         on-click-construct
                         on-click-state
                         on-link-analysis]
  (let [[load-state set-load-state] (react/useState {})]
    [:table {:class "ml-2"
             :style {:width "100%"
                     :border-collapse "collapse"}}
     [:tbody
      (for [{:keys [applied-analysis]
             construct-id :construct-id
             :as structured-analysis} suggested-structured-analyses
            :let [{:keys [state-id version-id]} applied-analysis
                  analysis-load-state (get load-state version-id)
                  {:keys [states construct]} structured-analysis
                  id->state (into {} states)]]
        [:tr
         {:key construct-id
          :style {:background-color "unset"}}
         [:td.p-0
          {:style {:border "none"}}
          [:a.is-flex
           {:title (if applied-analysis
                     "Click to view the applied analysis"
                     "Click to create the applied analysis")
            :on-click #(if applied-analysis
                         (on-click-construct construct-id applied-analysis)
                         (on-click-state construct-id applied-analysis))}
           (analysis.profile/format-analysis-id construct-id)
           [:span.ml-2 construct]]]
         [:td.p-0
          {:style {:border "none"}}
          [analysis.profile/state-tag {:class ["is-small" "ml-2"]} (id->state state-id)]]
         [:td.p-0
          {:style {:border "none"}}
          (let [linked-construct-ids (into #{} (map :construct-id (-> question :selected-answer :applied-analyses)))]
            [:div.is-flex
             [:button.button.is-rounded.is-circular.is-small.is-info.ml-2
              {:disabled (or (not applied-analysis)
                             (contains? linked-construct-ids construct-id))
               :title (cond
                        (not applied-analysis) "Applied analysis is missing, click on the construct to create applied analysis"
                        (contains? linked-construct-ids construct-id) "Applied analysis already linked"
                        :else "Link the sugegsted applied analysis to answer of question.\n\nCreates and selects an answer, if question has no answer yet.")
               :on-click #(on-link-analysis (fn [applied-analysis-version-ids state]
                                              (set-load-state (into {} (map (fn [version-id]
                                                                              [version-id state])
                                                                            applied-analysis-version-ids))))
                                            questionnaire-id
                                            (:id question)
                                            #{applied-analysis})}
              (if analysis-load-state
                [:div.loader]
                [:span.icon>i.fas.fa-link])]
             [:button.button.is-rounded.is-circular.is-small.ml-2
              {:title "Tell the system that the suggestion was not good"
               :on-click #(on-report-suggestion construct)}
              [:span.icon.i.fas.fa-regular.fa-thumbs-down]]])]])]]))

(defn BrowseAppliedAnaylsisButton
  [category applied-analyses questionnaire-id question analysis-search-component on-link-analysis]
  (let [[linking? set-linking] (react/useState false)]
    [:button.button.is-rounded.is-secondary.is-small
     {:on-click
      #(let [current-applied-analysis-version-ids (->> applied-analyses
                                                       (map :version-id)
                                                       set)]
         (rf/dispatch
          [::ui/push-modal
           {:component analysis-search-component
            :ctx {:applied-analysis-version-ids current-applied-analysis-version-ids
                  :category (if (= category "Other")
                              "Architecture" ;; Alphabetically first
                              category)
                  :on-submit (fn [checked-analyses]
                               (->> (mapv
                                     (fn [{:keys [construct-id
                                                  version-id
                                                  applied-analyses]
                                           :as sa}]
                                       {:construct-id construct-id
                                        :version-id (if applied-analyses
                                                      (-> applied-analyses
                                                          first
                                                          :version-id)
                                                      version-id)})
                                     checked-analyses)
                                    (on-link-analysis (fn [_applied-analysis-version-ids
                                                           state]
                                                        (set-linking state))
                                                      questionnaire-id
                                                      (:id question))))

                  :question question}
            :content-props {:style {:min-width "75vw" :max-height "90vh" :padding 0}}}]))
      :title "Browse applied analyses"}
     (if linking?
       [:div.loader]
       [:span.icon>i.fas.fa-folder-tree])]))

(defn AnaylsisCategoryTable [category
                             applied-analyses
                             selected-analyses
                             questionnaire-id
                             question
                             process-policy-pairs
                             all-linked-construct-ids
                             answer-id
                             overflow-disabled?
                             analysis-search-component
                             on-analysis-action
                             on-click-construct
                             on-click-state
                             on-link-analysis
                             on-unlink-analysis]
  (let [[unlinking-state set-unlinking-state] (react/useState {})
        [overflow-hidden? set-overflow-hidden] (react/useState (not overflow-disabled?))
        [action-component set-action-component] (react/useState nil)
        [modal-instance set-modal-instance] (react/useState (gensym))
        overflow-limit 10
        show-partial (and overflow-hidden?
                          (> (count applied-analyses) overflow-limit))]

    (react/useEffect
     (fn []
       (when-not action-component
        (->> "questions--analysis-context-menu"
             (.getElementsByClassName js/document)
             array-seq
             (mapv (fn [elem]
                     (aset (.-style elem) "left" "0")
                     (aset (.-style elem) "width" "100%"))))))
     #js [action-component])

    [:div.analysis-category-table
     [:div.level-left.mt-0.mb-1.is-flex
      [:f> BrowseAppliedAnaylsisButton category selected-analyses questionnaire-id question analysis-search-component on-link-analysis]

      [:h6.title.is-7.mb-0.mt-1.ml-2 category]]

     [:div.ml-1 (when (and (not overflow-disabled?)
                           (> (count applied-analyses) overflow-limit))
                  {:on-click #(when-not overflow-disabled?
                                (set-overflow-hidden (not overflow-hidden?)))
                   :style {:cursor (if overflow-hidden?
                                     "s-resize"
                                     "n-resize")}
                   :title (if show-partial
                            "Click to show all applied analyses linked to the selected answer"
                            "Click to hide rest of applied analyses linked to the selected answer")})
      [:table
       {:style {:width "100%"
                :border-collapse "collapse"}}
       [:tbody
        (let [rows (if show-partial
                     (take overflow-limit applied-analyses)
                     applied-analyses)]
          (doall (for [[idx {:keys [version-id next-version-id construct-id structured-analysis]
                             :as applied-analysis}] (map-indexed vector rows)
                       :let [{:keys [states]} structured-analysis
                             analysis-unlinking? (get unlinking-state version-id)
                             menu-ref (r/atom nil)
                             click-handler (fn [menu-action]
                                             (set-action-component
                                              ; this call returns a function so we wrap here
                                              ; to ensure that the function is stored in state
                                              ; and NOT immediately run against the set- fn!
                                              (fn [_] (on-analysis-action
                                                       menu-action
                                                       applied-analysis
                                                       @menu-ref
                                                       set-modal-instance))))]]
                   [:tr {:key (str version-id " - " idx)
                         :style {:background-color "unset"}}
                    [:td.p-0
                     {:style {:border "none"
                              :font-size "18px"}}
                     [AnalysisState (assoc applied-analysis :states states) on-click-state]]
                    [:td.p-0.questions--analysis-context-menu-trigger
                     {:style {:border "none"}}
                     [:f> popup-menu/popup-menu
                      {:suspend-click-to-close true
                       :modal-instance modal-instance
                       :trigger-class [:is-outlined :is-secondary :is-flex :is-flex-grow-1]
                       :trigger-content [AnalysisConstruct (merge structured-analysis applied-analysis) nil]
                       :trigger-component (fn [props & args]
                                            (into [:a (assoc props
                                                             :href "#"
                                                             :tab-index 0)]
                                                  args))
                       :menu-class :questions--analysis-context-menu
                       :on-close #(set-action-component nil)
                       :disabled false
                       :content [:div.questions--analysis-context-menu
                                 {:ref #(reset! menu-ref %)}
                                 (if action-component
                                   [:f> action-component]
                                   [:div.questions--analysis-context-menu-action-list
                                    {:class [:level-left :is-flex :my-0 :is-align-items-flex-start]}
                                    [:i.icon.fas.fa-chevron-right.mr-3.mt-0]
                                    [:div.is-flex.is-flex-direction-column
                                     [:div.title.is-6.mt-0.mb-2 "Analysis Actions"]
                                     [:button.button.is-secondary.is-outlined.my-1
                                      {:on-click (partial click-handler :answer-content)}
                                      [:i.icon.fas.fa-arrows-turn-to-dots.mr-2]
                                      "Insert Content from Answers"]

                                     [:hr.my-1]

                                     [:button.button.is-secondary.is-outlined.my-1
                                      {:disabled (not (boolean next-version-id))
                                       :on-click #(on-analysis-action :update-version applied-analysis @menu-ref set-modal-instance)}
                                      [:i.icon.fas.fa-square-pen.mr-2]
                                      "Update to Most Current Analysis"]

                                     ;; ;; TODO : LFA - Future Options
                                     ;; [:button.button.is-secondary.is-outlined
                                     ;;      "Search Related Documents"]
                                     ;;   [:button.button.is-secondary.is-outlined
                                     ;;    "Add Existing Evidence to Answer"]
                                     ;; [:hr.my-1]

                                     (when (#{"Policy" "Operations (process)"}
                                            category)
                                       (let [sibling
                                             (->> process-policy-pairs
                                                  (filterv
                                                   (fn [pair]
                                                     (->> (get pair (if (= category "Policy")
                                                                      :policy-construct-id
                                                                      :process-construct-id))
                                                          (= construct-id))))
                                                  first
                                                  ((if (= category "Policy")
                                                     :process-construct-id
                                                     :policy-construct-id)))
                                             already-added
                                             (->> sibling
                                                  (contains? all-linked-construct-ids))]
                                         [:button.button.is-secondary.is-outlined.my-1
                                          {:on-click (partial click-handler :process-policy-pair)
                                           :disabled already-added}
                                          [:i.icon.fas.fa-star-half-stroke.mr-2]
                                          (if already-added
                                            "Already Added"
                                            "Add")
                                          " Pair "
                                          (if (= category "Policy") "Process" "Policy")
                                          (when already-added
                                            [:<>
                                             (str " (" sibling ")")
                                             [:i.icon.fas.fa-circle-check.ml-1]])]))
                                     [:button.button.is-secondary.is-outlined.my-1
                                      {:on-click (partial click-handler :analysis-family)}
                                      [:i.icon.fas.fa-sitemap.mr-2]
                                      "Link Analysis Family"]
                                     [:hr.my-1]
                                     [:button.button.is-secondary.is-outlined.my-1
                                      {:on-click #(on-analysis-action :view-analysis applied-analysis @menu-ref)}
                                      [:i.icon.fas.fa-newspaper.mr-2]
                                      "View Analysis"]
                                     [:button.button.is-secondary.is-outlined.my-1
                                      {:disabled (:next-version-id applied-analysis)
                                       :on-click #(on-analysis-action :edit-analysis applied-analysis @menu-ref)}
                                      [:i.icon.fas.fa-pen-to-square.mr-2]
                                      "Edit Analysis"]]])]}]]
                    [:td.pt-1
                     {:style {:border "none"}}
                     [:div.is-flex
                      [:button.button.is-rounded.is-circular.is-small.is-danger.is-light
                       {:title "Unlink the applied analysis from the answer to the question"
                        :on-click (fn [e]
                                    (.stopPropagation e)
                                    (on-unlink-analysis questionnaire-id
                                                        (:id question)
                                                        answer-id
                                                        construct-id
                                                        version-id
                                                        (fn [linking?]
                                                          (set-unlinking-state (assoc unlinking-state
                                                                                      version-id
                                                                                      linking?)))))}
                       (if analysis-unlinking?
                         [:div.loader]
                         [:span.icon>i.fas.fa-unlink])]]]
                    (when (and show-partial
                               (= overflow-limit (inc idx)))
                      [:td.p-0
                       {:style {:border "none"}}
                       [:span
                        (str (count (drop overflow-limit applied-analyses))
                             " more...")]])])))]]]]))

(defn AnswerAppliedAnalyses
  [AnalysisSearch
   category
   {:keys [suggestion questionnaire-id] :as question}
   process-policy-pairs
   on-report-suggestion
   on-analysis-action
   on-click-construct
   on-click-state
   on-link-analysis
   on-unlink-analysis]

  [:div.questions--analysis
   (if suggestion
     (when-let [suggested-structured-analyses (-> suggestion :structured-analyses seq)]
       [:f> SuggestedAnalyses
        suggested-structured-analyses
        question
        questionnaire-id
        on-report-suggestion
        on-click-construct
        on-click-state
        on-link-analysis])
     [:f> AnaylsisCategoryTable
      (if (fn? category)
        "Other"
        category)
      (let [filter-fn (if (fn? category)
                        category
                        #(contains? % category))]
        (-> question
            :selected-answer
            :applied-analyses
            (cond->>
             (not (nil? category))
              (filter (fn [asa] (-> asa
                                    :structured-analysis
                                    :categories
                                    (->> (mapv :name))
                                    set
                                    filter-fn))))))
      (-> question
          :selected-answer
          :applied-analyses)
      questionnaire-id
      question
      process-policy-pairs
      (->> question
           :selected-answer
           :applied-analyses
           (mapv :construct-id)
           set)
      (-> question :selected-answer :id)
      (if suggestion true false)
      AnalysisSearch
      on-analysis-action
      on-click-construct
      on-click-state
      on-link-analysis
      on-unlink-analysis])])
